import { VocabularyRejectionCode, type VocabularyRejectedTerm } from '../../../../models/AccountVocabulary'
import { isApiErrors, isFetchBaseQueryError } from '../../../../models/ApiErrors'
import { type VocabularyTerm, type VocabularyTermSource } from './vocabularyTypes'

export const parseTermInput = (raw: string): string[] =>
  raw
    .split('\n')
    .map((candidate) => candidate.trim())
    .filter((candidate) => candidate.length > 0)

export const countWords = (term: string): number => term.trim().split(/\s+/).filter(Boolean).length

export const validateRename = (term: string, maxWords: number): string | undefined => {
  const trimmed = term.trim()
  if (trimmed.length === 0) {
    return 'Enter a word or phrase.'
  }
  if (countWords(trimmed) > maxWords) {
    return `Use at most ${maxWords} words.`
  }
  return undefined
}

export type RejectionReason = 'duplicate' | 'tooManyWords' | 'termTooLong' | 'overLimit'

export interface RejectedTerm {
  term: string
  reason: RejectionReason
}

const REJECTION_REASON_BY_CODE: Partial<Record<VocabularyRejectionCode, RejectionReason>> = {
  [VocabularyRejectionCode.DUPLICATE_IN_LIST]: 'duplicate',
  [VocabularyRejectionCode.DUPLICATE_OF_READ_ONLY]: 'duplicate',
  [VocabularyRejectionCode.PHRASE_TOO_LONG]: 'tooManyWords',
  [VocabularyRejectionCode.TERM_TOO_LONG]: 'termTooLong',
  [VocabularyRejectionCode.TOO_MANY_TERMS]: 'overLimit',
}

export const toRejectedTerms = (rejected: VocabularyRejectedTerm[]): RejectedTerm[] =>
  rejected.flatMap((entry) => {
    const reason = REJECTION_REASON_BY_CODE[entry.code]
    return reason ? [{ term: entry.term, reason }] : []
  })

const FIXABLE_ORDER: RejectionReason[] = ['tooManyWords', 'termTooLong', 'overLimit']

export const orderFixableTerms = (rejected: RejectedTerm[]): string[] =>
  FIXABLE_ORDER.flatMap((reason) => rejected.filter((entry) => entry.reason === reason).map((entry) => entry.term))

export const collectRejections = (rejected: RejectedTerm[], reason: RejectionReason): string[] =>
  rejected.filter((entry) => entry.reason === reason).map((entry) => entry.term)

export interface AddTermsResult {
  added: number
  rejected: RejectedTerm[]
}

export interface AddSummary {
  added: number
  duplicate: string[]
  tooManyWords: string[]
  termTooLong: string[]
  overLimit: string[]
}

export const buildAddSummary = (result: AddTermsResult): AddSummary => ({
  added: result.added,
  duplicate: collectRejections(result.rejected, 'duplicate'),
  tooManyWords: collectRejections(result.rejected, 'tooManyWords'),
  termTooLong: collectRejections(result.rejected, 'termTooLong'),
  overLimit: collectRejections(result.rejected, 'overLimit'),
})

export const hasFixableTerms = (summary: AddSummary): boolean =>
  summary.tooManyWords.length > 0 || summary.termTooLong.length > 0 || summary.overLimit.length > 0

export const hasSummaryToShow = (summary: AddSummary): boolean => hasFixableTerms(summary) || (summary.added === 0 && summary.duplicate.length > 0)

export const toVocabularyTerms = (source: VocabularyTermSource, terms: string[]): VocabularyTerm[] => terms.map((term) => ({ id: term, term, source }))

export const sortTerms = (terms: VocabularyTerm[]): VocabularyTerm[] => [...terms].sort((a, b) => a.term.localeCompare(b.term))

export const filterTerms = (terms: VocabularyTerm[], query: string): VocabularyTerm[] => {
  const trimmed = query.trim().toLowerCase()
  if (trimmed.length === 0) {
    return terms
  }
  return terms.filter((entry) => entry.term.toLowerCase().includes(trimmed))
}

export const describeVocabularyError = (error: unknown, maxWords: number): string => {
  if (!isFetchBaseQueryError(error)) {
    return 'Something went wrong. Please try again.'
  }
  const code = isApiErrors(error.data) ? error.data.errors[0]?.code : undefined
  if (error.status === 409 || code === VocabularyRejectionCode.DUPLICATE_OF_READ_ONLY) {
    return 'Already provided by Zocks or your enterprise.'
  }
  if (code === VocabularyRejectionCode.DUPLICATE_IN_LIST) {
    return 'Already in your vocabulary.'
  }
  if (code === VocabularyRejectionCode.PHRASE_TOO_LONG) {
    return `Use at most ${maxWords} words.`
  }
  if (code === VocabularyRejectionCode.TERM_TOO_LONG) {
    return 'That term is too long.'
  }
  if (code === VocabularyRejectionCode.TOO_MANY_TERMS) {
    return 'The term limit has been reached. Remove a term to make room.'
  }
  if (error.status === 403) {
    return 'Custom terms are managed at the enterprise level.'
  }
  if (error.status === 404) {
    return 'That term is no longer in your vocabulary.'
  }
  return 'Something went wrong. Please try again.'
}
